import { useMemo } from "react";
import { Card, Stack } from "@mui/material";
import Page from "../Page";
import { FEATURE } from "src/util/enums";
import { useAccess } from "src/hooks/useAccess.hook";
import { useQuery } from "@tanstack/react-query";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector } from "src/react-query/select";
import { BaseTable } from "../BaseTable/BaseTable";
import { userListColumns } from "./user.constant";
import { useNavigate } from "react-router-dom";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { useDispatch } from "react-redux";
import { useState, useEffect } from "react";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import ExportCSV from "src/components/ExportCsv/ExportCSV";
import Button from "@mui/material/Button";
import { Icon } from "@iconify/react";
import SendPromoModal from "./SendPromoModal";
import { useAuth } from "src/hooks/useAuth.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { is } from "src/util/is";
import { debounceFunc } from "src/utils/helper";

export default function CMCustomerList() {
  const { teamId } = useSelectedTeam();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchValue, setSearchValue] = useState("");
  
  const { data: customersResponse, isFetching } = useQuery(
    queryKeys.mobileCustomers(teamId, page, pageSize, searchValue),
    fetchWithError,
    {
      enabled: !is.empty(teamId),
      keepPreviousData: false,
      refetchOnMount: true,
    }
  );
  const customers = customersResponse?.data;
  const totalRecord = customersResponse?.totalRecord;
  const isReadOnly = useAccess(FEATURE.CUSTOMERS, "R");
  const isDisplayed = useAccess(FEATURE.PROMOCODE, 'V');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tableData, setTableData] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const user = useAuth();
  const companyType = useMemo(() => user?.user?.company?.companyType, [user]);

  const rowOrCellClickRedirect = ({ id }) => {
    // if (isReadOnly) return;
    dispatch(tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
      detailsId: id,
      tabOpenedFor: "customer",
      tabOpened: "general"
    }))
    navigate(`/cm-customer/${id}`);
  };

  const addNewCustomerBtnHandler = () => dispatch(tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
    detailsId: "",
    tabOpenedFor: "customer",
    tabOpened: "general"
  }))

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setPaginationModel({ page: newPage, pageSize: pageSize });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPage(0);
    setPageSize(newPageSize);
    setPaginationModel({ page: 0, pageSize: newPageSize });
  };

  useEffect(() => {
    if (!isFetching && customers?.length) {
      const data = customers.map((item) => ({
        ...item,
        fullName: `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim() || "-", 
      }));
      setTableData(data);
    } else {
      setTableData([]);
    }
  }, [isFetching, customersResponse, customers, totalRecord, page, pageSize, searchValue]);

  const handleSearching = (event) => {
    debounceFunc(() => {
      setPaginationModel({ page: 0, pageSize: pageSize });
      setPage(0);
      setSearchValue(event.target.value);
    }, 800)();
  };

  return (
    <Page
      title="Customer | Smart-CMS"
    >
      <Card sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <CommonSearchbar
            placeholder="Search"
            searchFunc={(e) => handleSearching(e)}
          />
          <Stack direction="row" spacing={2} alignItems="center">
            { isDisplayed && !isReadOnly && 
              <Button
                variant="contained"
                color="primary"
                startIcon={<Icon icon="eva:paper-plane-fill" />}
                disabled={selectedCustomers.length === 0}
                onClick={() => setIsPromoModalOpen(true)}
              >
                {`Send Promo ${selectedCustomers.length ? `(${selectedCustomers.length})` : ""}`}
              </Button>
            }
            <ExportCSV
              rows={tableData || []}
              columns={userListColumns(isReadOnly)}
              csvMorphemes="cm_customers"
              csvColumns={userListColumns(isReadOnly)}
            />
          </Stack>
        </Stack>
        <BaseTable
          rows={tableData ?? []}
          columns={userListColumns(isReadOnly, companyType)}
          loading={isFetching}
          getRowId={(row) => row.id}
          pageSize={pageSize}
          page={page}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          paginationModel={paginationModel}
          totalRows={totalRecord || 0}
          serverRowFetcher={true}
          rowsPerPageOptions={[10, 20, 50, 100]}
          showExportCsvBtn={false}
          csvMorphemes="cm_customers"
          rowOrCellClickRedirect={rowOrCellClickRedirect}
          isReadOnly={isReadOnly}
          csvColumns={userListColumns(isReadOnly)}
          enableSelection={true}   // ✅ only when you want selection
          onSelectionChange={(selected) => {
            setSelectedCustomers(selected);
          }}
        />
      </Card>

      {/* Send Promo Modal */}
      { isDisplayed && !isReadOnly &&
        <SendPromoModal
          open={isPromoModalOpen}
          onClose={() => setIsPromoModalOpen(false)}
          selectedCustomers={selectedCustomers}
        />
      }
    </Page>
  );
}
